import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useOUItems } from '@/hooks/useOUs';
import { useUsersBatchLookup } from '@/hooks/useUsers';

/**
 * Resolución de nombres para los reportes de analítica.
 *
 * AnalyticsService devuelve identificadores, nunca nombres: el catálogo de OUs y el padrón de
 * personas los administra UserAdminService. Sin esta resolución las tablas mostraban GUIDs, que
 * es lo que veía el usuario en Score por unidad, Omisiones por unidad y el ranking generacional.
 */

/** Etiqueta de una OU a partir de su id; cae al id cuando la OU no está en el catálogo. */
export function useOuNameResolver() {
  const { t } = useTranslation();
  // Bug UAT (17-ago-2026) — `rootsOnly` defaultea a `true`, así que el mapa solo tenía las seis OUs
  // raíz y CUALQUIER OU hija caía al `?? ouId` de abajo: en el Mapa de calor se veía una fila
  // titulada con un GUID crudo entre EUROPA/LATAM/MEXICO/… Aquí se necesita el catálogo COMPLETO —
  // esto no es un selector que el usuario navegue, es un diccionario id→nombre, y una OU que no
  // esté en él no se oculta: se delata. `activeOnly: false` va por lo mismo: una OU desactivada
  // ayer sigue apareciendo en los agregados de la semana pasada y también hay que saber nombrarla.
  // No cuesta una llamada extra: `getOUs()` ya trae todo el catálogo y el filtro de raíces es
  // del lado del cliente.
  const { data: ouItems = [], isLoading } = useOUItems({ activeOnly: false, rootsOnly: false });

  const nameById = useMemo(
    () => new Map(ouItems.map((ouItem) => [ouItem.id, ouItem.name])),
    [ouItems],
  );

  const resolveOuName = useCallback(
    (ouId: string | undefined | null): string => {
      if (!ouId || ouId.trim().length === 0) return t('analytics.heatmap.noOu');
      return nameById.get(ouId) ?? ouId;
    },
    [nameById, t],
  );

  return { resolveOuName, isLoadingOuNames: isLoading };
}

/**
 * Nombre de cada persona de `personIds`, resuelto en una sola llamada por lote. Se pide solo por
 * los identificadores que la vista va a dibujar y no el padrón completo del tenant, que en este
 * despliegue son más de diez mil registros paginados de doscientos en doscientos.
 */
export function usePersonNameResolver(personIds: string[]) {
  const uniqueIds = useMemo(() => [...new Set(personIds.filter(Boolean))].sort(), [personIds]);
  const { data: lookup = [], isLoading } = useUsersBatchLookup(uniqueIds);

  const nameById = useMemo(
    () => new Map(lookup.map((person) => [person.id, person.displayName])),
    [lookup],
  );

  const resolvePersonName = useCallback(
    (personId: string): string => nameById.get(personId) ?? personId,
    [nameById],
  );

  return { resolvePersonName, isLoadingPersonNames: isLoading };
}
